import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Grid,
    Table,
    TableBody,
    TableHead,
    TableRow,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import axios from 'axios';
import toast from 'react-hot-toast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useHistory, useLocation } from 'react-router-dom';
import ErrorFallback from 'components/error-fallback';
import { ErrorBoundary } from 'react-error-boundary';
import { endPoints } from 'pages/dlops-dashboard/api/endpoint';
import { PEPVIGIL } from 'pages/dlops-dashboard/utils/constant';
import { commonStyles as styles } from 'assets/style';
import { Item, StyledTableCell, ProgressIcon, style } from '../style';
import AccessToken from '../components/accessToken/accessToken';
import StyleTextField from '../styleTextField';
import breadcrumbIcon from '../../../assets/images/dlopsImages/arrow_forward.svg';

const CreateNewModelTags = () => {
    const { getAccessToken } = AccessToken();
    const [addtags, setAddTags] = useState([]);
    const history = useHistory();
    const location = useLocation();
    const { projectDetails } = location.state || {};
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [deleteOpenPopUp, setDeletePopUp] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [allSelected, setAllSelected] = useState(false);
    const [errors, setErrors] = useState({});
    const [retrainingTagsErrors, setRetrainingTagsErrors] = useState({});
    const [smartSamplingTagsErrors, setSmartSamplingTagsErrors] = useState({});
    const [smartFilteringErrors, setSmartFilteringErrors] = useState({});
    const [openPopup, setOpenPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [showAccessFields, setShowAccessFields] = useState(false);
    const [isSmartFilteringEnabled, setIsSmartFilteringEnabled] = useState(false);
    const moduleInfo = JSON.parse(localStorage.getItem('addNewModel')) || {};
    const modelTypeTag = moduleInfo?.tags?.find((tag) => tag.mkey === 'model_type');
    const isSegmentation = modelTypeTag?.mvalue === 'Segmentation';
    const [tags, setTags] = useState([
        {
            name: 'BaseLine Datasource',
            mkey: 'baseline_datasource',
            tooltipInfo: 'A URL that provides access to a CSV file containing details about the training dataset used for the model',
            mvalue: '',
        },
        {
            name: 'Baseline Datasource Version',
            mkey: 'baseline_datasource_version',
            tooltipInfo:
                'The version number of the baseline datasource, indicating the specific iteration or update of the CSV file containing the training dataset details',
            mvalue: '',
        },
        {
            name: 'Storage Account',
            mkey: 'storage_account',
            tooltipInfo:
                'An identifier for the cloud storage account used to store data and files associated with the project, including models, datasets, and other resources',
            mvalue: '',
        },
        {
            name: 'Market',
            mkey: 'market',
            tooltipInfo:
                'The specific market or environment in which the model is being deployed for use, indicating the target audience or application context',
            mvalue: '',
        },
        {
            name: 'Container Name',
            mkey: 'container_name',
            tooltipInfo: 'The name of the storage container used to organize and manage data and files within a cloud storage account',
            mvalue: '',
        },
        { name: 'Framework', mkey: 'framework', tooltipInfo: 'The software framework or library used to develop and implement the model', mvalue: '' },
        {
            name: 'Drift Distance Threshold',
            mkey: 'drift_distance_threshold',
            tooltipInfo:
                'Set the threshold value for the drift monitor. If the drift distance exceeds this value, the monitor will trigger. The default value as per Arize is 2.',
            mvalue: 2,
        },
    ]);

    const [smartFiltering, setSmartFiltering] = useState([
        {
            name: 'Exported CSV File Location',
            mkey: 'output_csv_path',
            tooltipInfo: 'Defines the location where the new CSV file, generated after the smart filtering process, will be stored.',
            mvalue: '',
            helpText: 'Example: https://storageaccount.blob.core.windows.net/container-name/folder/file.csv',
        },
        {
            name: 'Processed Images Folder Path',
            mkey: 'images_output_path',
            tooltipInfo: 'Specifies the directory used to store the compressed (JPEG) images produced during the smart filtering process.',
            mvalue: '',
            helpText: 'Example: https://storageaccount.blob.core.windows.net/container-name/folder',
        },
    ]);
    const [retrainingTags, setRetrainingTags] = useState([
        {
            name: 'Retraining Storage Account',
            mkey: 'retraining_storage_acc_name',
            tooltipInfo: 'Name of the Azure Storage Account.',
            mvalue: '',
        },
        {
            name: 'Retraining Container',
            mkey: 'retraining_container_name',
            tooltipInfo: 'Name of the blob container to store drifted images.',
            mvalue: '',
        },
        {
            name: 'Retraining Destination Path',
            mkey: 'retraining_blob_path',
            tooltipInfo: 'Subdirectory within the container where images will be saved.',
            mvalue: '',
        },
        {
            name: 'Retraining SAS Token',
            mkey: 'retraining_sas_token',
            tooltipInfo: 'Shared Access Signature token for secure access.',
            mvalue: '',
        },
    ]);
    const [smartSampling, setSmartSampling] = useState([
        {
            name: 'Source Account',
            mkey: 'source_account',
            tooltipInfo: 'Source storage account for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Source Container',
            mkey: 'source_container',
            tooltipInfo: 'Source container for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Source Subpath',
            mkey: 'source_subpath',
            tooltipInfo: 'Source subpath for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Output Account',
            mkey: 'output_account',
            tooltipInfo: 'Output storage account for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Output Container',
            mkey: 'output_container',
            tooltipInfo: 'Output container for smart sampling.',
            mvalue: '',
        },
        {
            name: 'Output Subpath',
            mkey: 'output_subpath',
            tooltipInfo: 'Output subpath for smart sampling.',
            mvalue: '',
        },
    ]);
    // // Helper function to validate URLs
    const isValidURL = (url) => {
        const pattern = /^https:\/\/[a-zA-Z0-9_-]+\.blob\.core\.windows\.net\/[a-zA-Z0-9_-]+(\/[a-zA-Z0-9_.-]+)*\/[a-zA-Z0-9_.-]+\.[a-z]+$/;
        return pattern.test(url);
    };
    useEffect(() => {
        const savedAllTags = localStorage.getItem('allModelTags');
        const savedDefaultTags = localStorage.getItem('adddNewDefaulttags');
        const savedNewlyAddedTags = localStorage.getItem('addNewlyAddedtags');

        // For backward compatibility, check both the new consolidated storage and old separate storage
        if (savedAllTags) {
            const allTagsData = JSON.parse(savedAllTags);
            setTags(allTagsData.tags || []);
            setAddTags(allTagsData.addtags || []);
            setRetrainingTags(allTagsData.retrainingTags || []);
            setRetrainingTags(allTagsData.smartSampling || []);
            setSmartFiltering(allTagsData.smartFiltering || []);
            setIsSmartFilteringEnabled(allTagsData.isSmartFilteringEnabled || false);
        } else {
            // Fallback to old storage format
            if (savedDefaultTags) {
                setTags(JSON.parse(savedDefaultTags));
            }
            if (savedNewlyAddedTags) {
                setAddTags(JSON.parse(savedNewlyAddedTags));
            }
        }
    }, []);

    const handleAddNewRow = () => {
        setAddTags([...addtags, { name: '', type: '', value: '', checked: false }]);
    };

    const handleClosePopup = () => {
        setOpenPopup(false);
        setShowAccessFields(false);
        if (isSuccess) {
            localStorage.removeItem('create_project');
            localStorage.removeItem('addNewModel');
            localStorage.removeItem('adddNewDefaulttags');
            localStorage.removeItem('addNewlyAddedtags');
            localStorage.removeItem('allModelTags');
            // Remove temporary submission items
            localStorage.removeItem('addNewtags');
            localStorage.removeItem('addNewRetrainingTags');
            localStorage.removeItem('addNewSmartFilteringTags');
            history.push('/pepvigil-registered-models');
        }
    };
    const handleCancel = () => {
        history.push('/pepvigil-registered-models');
    };
    const handleBack = () => {
        history.push('/pepvigil-add-new-model');
    };
    const handleInputChange = (index, field, value) => {
        const updatedTags = [...tags];
        if (updatedTags[index].mkey === 'drift_distance_threshold' && field === 'mvalue') {
            updatedTags[index][field] = value === '' ? '' : Number(value);
        } else {
            updatedTags[index][field] = value;
        }
        setTags(updatedTags);
        if (value) {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[index];
                return newErrors;
            });
        }
    };

    const handleRetrainingInputChange = (index, field, value) => {
        const updatedTags = [...retrainingTags];
        updatedTags[index][field] = value;
        setRetrainingTags(updatedTags);

        // Clear error if input is filled
        if (value) {
            setRetrainingTagsErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[`retraining_${index}`]; // Remove the error for this index
                return newErrors;
            });
        }
    };

    const handleSamplingInputChange = (index, field, value) => {
        const updatedTags = [...smartSampling];
        updatedTags[index][field] = value;
        setSmartSampling(updatedTags);

        // Clear error if input is filled
        if (value) {
            setSmartSamplingTagsErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[`sampling_${index}`]; // Remove the error for this index
                return newErrors;
            });
        }
    };
    const handleSmartFilteringInputChange = (index, field, value) => {
        const updatedTags = [...smartFiltering];
        updatedTags[index][field] = value;
        setSmartFiltering(updatedTags);

        const otherIndex = index === 0 ? 1 : 0;
        const otherFieldValue = updatedTags[otherIndex].mvalue; // Use updatedTags instead of smartFiltering

        // Clear errors for both fields first
        setSmartFilteringErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            delete newErrors[otherIndex];
            return newErrors;
        });

        // Set new errors if needed
        if (value.trim() && !otherFieldValue.trim()) {
            setSmartFilteringErrors((prevErrors) => ({
                ...prevErrors,
                [otherIndex]: 'This field is required when other smart filtering fields are filled.',
            }));
        }

        // Validate URL for output_csv_path
        if (updatedTags[index].mkey === 'output_csv_path' && value && !isValidURL(value)) {
            setSmartFilteringErrors((prevErrors) => ({
                ...prevErrors,
                [index]: 'Please enter a valid CSV file URL.',
            }));
        }
    };
    const handleNavigation = () => {
        history.push('/pepvigil-projects');
    };
    const handleInputTags = (index, field, value) => {
        const updatedRows = addtags.map((row, i) => (i === index ? { ...row, [field]: value } : row));
        setAddTags(updatedRows);
    };
    const handleRowCheck = (index) => {
        const updatedRows = addtags.map((row, i) => (i === index ? { ...row, isChecked: !row.isChecked } : row));
        setAddTags(updatedRows);
    };

    const handleDeleteRow = (index) => {
        setDeleteIndex(index);
        setDeletePopUp(true);
    };
    const deleteRow = (index) => {
        const updatedRows = addtags.filter((_, i) => i !== index);
        setAddTags(updatedRows);
    };
    const handleCancelDelete = () => {
        setDeletePopUp(false);
    };
    const handleDeleteAllSelected = () => {
        setDeletePopUp(true);
        setDeleteIndex('SelectedAll');
    };
    const handleSelectAll = (event) => {
        const { checked } = event.target;
        setAllSelected(checked);
        setIsAllChecked(checked);
        setAddTags(addtags.map((row) => ({ ...row, checked, isChecked: checked })));
    };
    const handleConfirmDelete = () => {
        if (deleteIndex === 'SelectedAll') {
            setAddTags(addtags.filter((row) => !row.isChecked));
        } else {
            deleteRow(deleteIndex);
        }
        setDeletePopUp(false);
        setAllSelected(false);
        setIsAllChecked(false);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100vh' }}>
                <CircularProgress color="inherit" size={16} sx={ProgressIcon.circularProgressColor} />
                <Typography variant="body2" sx={ProgressIcon.circularProgressColor} style={{ marginLeft: '10px' }}>
                    Loading
                </Typography>
            </Box>
        );
    }

    const handleValidation = () => {
        let formIsValid = true;
        const newErrors = {};
        const newRetrainingTagsErrors = {};
        const newSmartSamplingTagsErrors = {};
        const smartFilteringTagsErrors = {};
        if (isSmartFilteringEnabled) {
            const filledFieldsCount = smartFiltering.filter((tag) => tag.mvalue.trim() !== '').length;
            smartFiltering.forEach((tag, index) => {
                const isFieldEmpty = !tag.mvalue.trim();
                const otherFieldsFilled = filledFieldsCount > (isFieldEmpty ? 0 : 1); // Check if other fields are filled

                if (isFieldEmpty && otherFieldsFilled) {
                    formIsValid = false;
                    smartFilteringTagsErrors[index] = 'This field is required when smart filtering is enabled and other fields are filled.';
                } else if (tag.mkey === 'output_csv_path' && tag.mvalue && !isValidURL(tag.mvalue)) {
                    formIsValid = false;
                    smartFilteringTagsErrors[index] = 'Please enter a valid CSV file URL.';
                }
            });
        }
        tags.forEach((tag, index) => {
            switch (tag.mkey) {
                case 'baseline_datasource':
                    if (!tag.mvalue) {
                        formIsValid = false;
                        newErrors[index] = 'Baseline datasource is required.';
                    } else if (!isValidURL(tag.mvalue)) {
                        formIsValid = false;
                        newErrors[index] = 'Please enter a valid URL.';
                    }
                    break;
                case 'output_csv_path':
                    if (!isValidURL(tag.mvalue)) {
                        formIsValid = false;
                        newErrors[index] = 'Please enter a valid URL.';
                    }
                    break;
                case 'images_output_path':
                    if (!isValidURL(tag.mvalue)) {
                        formIsValid = false;
                        newErrors[index] = 'Please enter a valid URL.';
                    }
                    break;

                case 'baseline_datasource_version':
                    if (!tag.mvalue) {
                        formIsValid = false;
                        newErrors[index] = 'Baseline datasource version is required.';
                    } else if (!/^\d+(\.\d+)?$/.test(tag.mvalue)) {
                        formIsValid = false;
                        newErrors[index] = 'Please enter a valid number or decimal.';
                    }
                    break;
                case 'desc_version':
                    if (!tag.mvalue) {
                        formIsValid = false;
                        newErrors[index] = 'Description version is required.';
                    }
                    break;
                case 'description_model':
                case 'storage_account':
                case 'market':
                case 'container_name':
                case 'framework':
                    if (!tag.mvalue) {
                        formIsValid = false;
                        newErrors[index] = `${tag.mkey.replace(/_/g, ' ')} is required.`;
                    }
                    break;
                case 'drift_distance_threshold': {
                    const thresholdValue = Number(tag.mvalue);

                    if (Number.isNaN(thresholdValue) || thresholdValue < 0.5 || thresholdValue > 5) {
                        formIsValid = false;
                        newErrors[index] = 'Please enter a number between 0.5 and 5';
                    }
                    break;
                }
                default:
                    break;
            }
        });

        addtags.forEach((tag, index) => {
            if (!tag.name) {
                formIsValid = false;
                newErrors[`add_${index}_name`] = 'Name is required.';
            }
            if (!tag.value) {
                formIsValid = false;
                newErrors[`add_${index}_value`] = 'Value is required.';
            }
        });

        retrainingTags.forEach((tag, index) => {
            if (!tag.mvalue) {
                formIsValid = false;
                newRetrainingTagsErrors[`retraining_${index}`] = `${tag.mkey.replace(/_/g, ' ')} is required.`;
            }
        });
        if (isSegmentation) {
            smartSampling.forEach((tag, index) => {
                if (!tag.mvalue) {
                    formIsValid = false;
                    newSmartSamplingTagsErrors[`sampling_${index}`] = `${tag.mkey.replace(/_/g, ' ')} is required.`;
                }
            });
        } else {
            Object.keys(newSmartSamplingTagsErrors).forEach((key) => delete newSmartSamplingTagsErrors[key]);
        }
        setErrors(newErrors);
        setRetrainingTagsErrors(newRetrainingTagsErrors);
        setSmartSamplingTagsErrors(newSmartSamplingTagsErrors);
        setSmartFilteringErrors(smartFilteringTagsErrors);
        return formIsValid;
    };

    const saveNewProjectCreation = async (accessTokenPromise, payload) => {
        try {
            const accessToken = await accessTokenPromise;
            const response = await axios.post(endPoints.submitCreateNewProject, payload, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'application/json',
                },
            });
            return response?.data;
        } catch (error) {
            return error;
        }
    };
    const handleSaveTags = async () => {
        if (handleValidation()) {
            const allTagsData = {
                tags,
                addtags,
                retrainingTags,
                smartSampling,
                smartFiltering,
                isSmartFilteringEnabled,
            };

            localStorage.setItem('allModelTags', JSON.stringify(allTagsData));

            toast.success('Meta Data Tags Saved Successfully', {
                position: 'top-right',
                autoClose: 3000,
            });
        }
    };
    const handleSubmit = async () => {
        if (handleValidation()) {
            const tagsToSave = tags.map(({ mkey, mvalue }) => ({
                mkey,
                mvalue: (mkey === 'output_csv_path' || mkey === 'images_output_path') && !mvalue ? 'N/A' : mvalue,
            }));
            const retrainingTagsToSave = retrainingTags.map(({ mkey, mvalue }) => ({ mkey, mvalue }));
            const smartSamplingTagsToSave = smartSampling.map(({ mkey, mvalue }) => ({ mkey, mvalue }));

            // Include smart filtering tags regardless of whether it's enabled
            const smartFilteringTagsToSave = smartFiltering.map(({ mkey, mvalue }) => ({
                mkey,
                mvalue: !mvalue ? 'N/A' : mvalue,
            }));

            localStorage.setItem('addNewtags', JSON.stringify(tagsToSave));
            localStorage.setItem('addNewRetrainingTags', JSON.stringify(retrainingTagsToSave));
            localStorage.setItem('addNewSmartSamplingTags', JSON.stringify(smartSamplingTagsToSave));
            localStorage.setItem('addNewSmartFilteringTags', JSON.stringify(smartFilteringTagsToSave));

            const storedProjectDetails = projectDetails[0];
            const addNewModel = JSON.parse(localStorage.getItem('addNewModel')) || {};
            const tagsFromStorage = JSON.parse(localStorage.getItem('addNewtags')) || [];
            const retrainingTagsFromStorage = JSON.parse(localStorage.getItem('addNewRetrainingTags')) || [];
            const smartSamplingTagsFromStorage = JSON.parse(localStorage.getItem('addNewSmartSamplingTags')) || [];
            const smartFilteringTagsFromStorage = JSON.parse(localStorage.getItem('addNewSmartFilteringTags')) || [];
            const projectNameTag = {
                mkey: 'project_name',
                mvalue: storedProjectDetails.name,
            };
            const addNewtagsToSave = addtags.map(({ name, value }) => ({
                mkey: name,
                mvalue: value,
            }));
            const payload = {
                project_name: storedProjectDetails.name || '',
                new_project: false,
                project_desc: storedProjectDetails.description || '',
                project_createdby: storedProjectDetails.createdby_user || '',
                project_createdby_user_details: {
                    name: storedProjectDetails?.created_by_user_name || null,
                    mail_address: storedProjectDetails?.created_by_user_email || null,
                },
                model_name: addNewModel.model_name || '',
                model_version: String(addNewModel.model_version) || '',
                model_desc: addNewModel.model_desc || '',
                model_createdby: addNewModel.model_createdby || '',
                model_createdby_user_details: addNewModel.model_createdby_user_details,
                model_current_stage: addNewModel.model_current_stage,
                tags: [
                    ...tagsFromStorage,
                    ...addNewModel.tags,
                    projectNameTag,
                    ...addNewtagsToSave,
                    ...retrainingTagsFromStorage,
                    ...smartFilteringTagsFromStorage,
                    ...smartSamplingTagsFromStorage,
                ],
            };
            try {
                setLoading(true);
                const response = await saveNewProjectCreation(getAccessToken(), payload);

                if (response && response.Response === 'Success') {
                    setPopupMessage(<>New Model added to project successfully.</>);
                    setIsSuccess(true);
                } else {
                    setPopupMessage('Failed to create project.');
                    setIsSuccess(false);
                }
                setLoading(false);
                setOpenPopup(true);
            } catch (error) {
                setPopupMessage('Error saving data.');
                setIsSuccess(false);
                setLoading(false);
                setOpenPopup(true);
            }
        }
    };

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            {' '}
            <Grid container>
                <Grid item xs={12}>
                    <Typography variant="h5">
                        <Item>
                            <div
                                style={{
                                    marginTop: '8px',
                                    fontSize: '14px',
                                    textAlign: 'left',
                                }}
                                className="contentContainer"
                            >
                                <span>{PEPVIGIL}</span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span aria-hidden="true" style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleNavigation()}>
                                    Projects
                                </span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span aria-hidden="true" style={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => handleBack()}>
                                    Add New Model
                                </span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span style={{ fontWeight: 'bold', color: '#005CBC' }}>Add New Model Tags</span>
                            </div>
                        </Item>
                    </Typography>
                </Grid>
            </Grid>
            <Box className="contentContainer">
                <Box
                    sx={{
                        top: '164px',
                        height: '70px',
                        background: '#FFFFFF 0% 0% no-repeat padding-box',
                        borderRadius: '4px',
                        opacity: 1,
                        marginTop: '20px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography sx={{ fontSize: '18px', fontWeight: '700', color: '#0047BA' }}>Add New Model Tags</Typography>
                </Box>
                <Dialog
                    open={openPopup}
                    onClose={handleClosePopup}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    PaperProps={{
                        sx: {
                            width: 600,
                            height: 'auto',
                            borderRadius: '8px',
                        },
                    }}
                >
                    <Box sx={styles.modalWrapper}>
                        <DialogContent sx={{ padding: '0px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                {!showAccessFields && (
                                    <>
                                        {isSuccess && (
                                            <CheckCircleIcon
                                                sx={{
                                                    fontSize: '50px',
                                                    color: '#005CBC',
                                                    marginBottom: '0px',
                                                }}
                                            />
                                        )}
                                        <DialogTitle
                                            id="alert-dialog-title"
                                            sx={{
                                                ...styles.modalTitle,
                                                color: isSuccess ? 'black' : 'red',
                                            }}
                                        >
                                            {isSuccess ? 'Success' : 'Failed'}
                                        </DialogTitle>
                                        <Typography
                                            sx={{
                                                ...styles.modalContent,
                                                color: isSuccess ? 'black' : 'red',
                                                textAlign: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {popupMessage}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                justifyContent: 'center',
                                padding: '16px',
                            }}
                        >
                            <Button
                                onClick={handleClosePopup}
                                disableElevation
                                disableRipple
                                sx={{ ...styles.modalSaveButton, height: '36px', width: '160px' }}
                            >
                                Okay
                            </Button>
                        </DialogActions>
                    </Box>
                </Dialog>
                <Box
                    sx={{
                        top: '164px',
                        background: '#FFFFFF 0% 0% no-repeat padding-box',
                        borderRadius: '4px',
                        opacity: 1,
                        marginTop: '20px',
                        padding: '16px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '1140px',
                                paddingBottom: '10px',
                            }}
                        >
                            <Typography
                                sx={{
                                    marginRight: '10px',
                                    fontSize: '16px',
                                    color: '#0047BA',
                                    fontWeight: '500',
                                }}
                            >
                                Meta Data Tags
                            </Typography>
                            <Box sx={{ display: 'flex', marginLeft: 'auto', gap: '10px' }}>
                                <Button
                                    sx={{ ...styles.modalSaveButton, height: '36px', width: '139px' }}
                                    disableElevation
                                    disableRipple
                                    variant="contained"
                                    onClick={handleAddNewRow}
                                >
                                    Add New Tag
                                </Button>
                                <Button
                                    sx={{ ...styles.modalSaveButton, height: '36px', width: '139px' }}
                                    disableElevation
                                    disableRipple
                                    variant="contained"
                                    onClick={handleSaveTags}
                                >
                                    Save Tags
                                </Button>
                                {allSelected && addtags.length >= 1 && (
                                    <Button
                                        sx={{
                                            ...styles.primaryMediumDangerButton,
                                            '&.MuiButton-root:hover': {
                                                backgroundColor: (theme) =>
                                                    `${theme.palette.background.$color_background_button_primary_danger_hover}!important`,
                                            },
                                            height: '36px',
                                            width: '187px',
                                        }}
                                        variant="contained"
                                        disableElevation
                                        disableRipple
                                        onClick={handleDeleteAllSelected}
                                    >
                                        Delete All Selected
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <Table sx={{ width: '83%' }}>
                            <TableHead sx={{ backgroundColor: (theme) => theme.palette.cyan['90'], borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                                <TableRow sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                                    <StyledTableCell sx={{ width: '40px', padding: '4px 8px', fontSize: '14px', borderBottom: 'none' }}>
                                        <Checkbox sx={{ color: '#005CBC' }} checked={isAllChecked} onChange={handleSelectAll} />
                                    </StyledTableCell>
                                    <StyledTableCell
                                        sx={{
                                            width: '200px',
                                            padding: '12px 12px',
                                            color: '#005CBC',
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            borderBottom: 'none',
                                        }}
                                    >
                                        Tag Name
                                    </StyledTableCell>
                                    <StyledTableCell
                                        sx={{
                                            width: '100px',
                                            padding: '12px 12px',
                                            color: '#005CBC',
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            borderBottom: 'none',
                                        }}
                                    >
                                        Value
                                    </StyledTableCell>
                                    <StyledTableCell
                                        sx={{
                                            width: '100px',
                                            padding: '12px 12px',
                                            color: '#005CBC',
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            borderBottom: 'none',
                                        }}
                                    />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tags.map((row, index) => (
                                    <TableRow key={index} sx={{ height: '40px', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                                        <StyledTableCell sx={{ width: '40px', padding: '12px 12px', borderBottom: 'none' }} />
                                        <StyledTableCell sx={{ padding: '12px 12px', width: '200px', borderBottom: 'none' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {!['drift_distance_threshold', 'output_csv_path', 'images_output_path'].includes(row.mkey) ? (
                                                    <Typography sx={{ color: '#E4080A', marginRight: '4px' }}>*</Typography>
                                                ) : (
                                                    <Box sx={{ width: '10px' }} />
                                                )}
                                                {row.name}
                                                {row.tooltipInfo && (
                                                    <Tooltip
                                                        title={row.tooltipInfo}
                                                        arrow
                                                        componentsProps={{
                                                            tooltip: { sx: { backgroundColor: '#272727', fontSize: '10px' } },
                                                            arrow: { sx: { color: '#272727' } },
                                                        }}
                                                    >
                                                        <InfoOutlinedIcon
                                                            sx={{
                                                                fontSize: '16px',
                                                                color: '#005CBC',
                                                                marginLeft: '8px',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell
                                            sx={{
                                                padding: '12px',
                                                width: '100px',
                                                borderBottom: 'none',
                                            }}
                                        >
                                            <StyleTextField
                                                value={row.mvalue}
                                                onChange={(e) => handleInputChange(index, 'mvalue', e.target.value)}
                                                variant="filled"
                                                fullWidth={['baseline_datasource', 'output_csv_path', 'images_output_path'].includes(row.mkey)}
                                                sx={{
                                                    '&.MuiTextField-root': { border: 'none' },
                                                    width: ['baseline_datasource', 'output_csv_path', 'images_output_path'].includes(row.mkey)
                                                        ? '500px'
                                                        : '200px',
                                                    border: `1px solid ${errors[index] ? '#d40020' : '#1378D4'}`,
                                                    borderRadius: '4px',
                                                }}
                                                inputProps={{ style: { padding: '4px' } }}
                                            />
                                            {errors[index] && <Typography sx={{ color: '#d40020', fontSize: '12px', mt: '4px' }}>{errors[index]}</Typography>}
                                            {row.mkey === 'baseline_datasource' && (
                                                <Typography sx={{ fontSize: '10px', color: 'grey', mt: '4px' }}>
                                                    Example:
                                                    https://iaimlops.blob.core.windows.net/dlops-container/OC/baseline/dlops-model-oc/baseline_dlops-oc_20240425.csv
                                                </Typography>
                                            )}
                                        </StyledTableCell>
                                    </TableRow>
                                ))}
                                {addtags &&
                                    addtags.length > 0 &&
                                    addtags.map((row, index) => (
                                        <TableRow key={index} sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                                            <StyledTableCell sx={{ width: '40px', padding: '12px 12px', borderBottom: 'none' }}>
                                                <Checkbox checked={row.isChecked} onChange={() => handleRowCheck(index)} />
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ padding: '12px 12px', width: '200px', borderBottom: 'none' }}>
                                                <StyleTextField
                                                    value={row.name}
                                                    onChange={(e) => handleInputTags(index, 'name', e.target.value)}
                                                    variant="filled"
                                                    sx={{
                                                        marginTop: '8px',
                                                    }}
                                                    inputProps={{
                                                        style: { padding: '8px 0' },
                                                    }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ padding: '12px 12px', width: '100px', borderBottom: 'none' }}>
                                                <StyleTextField
                                                    value={row.value}
                                                    onChange={(e) => handleInputTags(index, 'value', e.target.value)}
                                                    variant="filled"
                                                    sx={{
                                                        marginTop: '8px',
                                                    }}
                                                    inputProps={{
                                                        style: { padding: '8px 0' },
                                                    }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ width: '100px', padding: '12px 12px', borderBottom: 'none' }}>
                                                {row.isChecked && (
                                                    <Typography
                                                        onClick={() => handleDeleteRow(index)}
                                                        sx={{ color: '#005CBC', textDecoration: 'underline', cursor: 'pointer' }}
                                                    >
                                                        Delete
                                                    </Typography>
                                                )}
                                            </StyledTableCell>
                                        </TableRow>
                                    ))}
                                <StyledTableCell sx={{ padding: '12px 12px', width: '100%' }} colSpan={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Tooltip
                                            title={
                                                <>
                                                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                                        Optimizes image size and standardizes formats to enable faster processing and improve model performance.{' '}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '12px', marginTop: '4px', fontWeight: '600' }}>
                                                        Use this only when the dataset contains images larger than 3 MB.
                                                    </Typography>
                                                </>
                                            }
                                            arrow
                                            placement="top-end"
                                        >
                                            <Typography
                                                sx={{
                                                    marginRight: '10px',
                                                    fontSize: '16px',
                                                    color: '#0047BA',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                Enable Smart Filtering
                                                <Checkbox
                                                    size="small"
                                                    color="primary"
                                                    checked={isSmartFilteringEnabled}
                                                    onChange={(e) => setIsSmartFilteringEnabled(e.target.checked)}
                                                />
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </StyledTableCell>
                                {isSmartFilteringEnabled &&
                                    smartFiltering?.map((row, index) => {
                                        const hasAnyValue = smartFiltering.some((tag) => tag.mvalue.trim() !== '');
                                        const isRequired = hasAnyValue;

                                        return (
                                            <TableRow key={`smart-${index}`} sx={{ height: '40px' }}>
                                                <StyledTableCell sx={{ width: '40px', padding: '12px 12px' }} />
                                                <StyledTableCell sx={{ padding: '12px 12px', width: '200px' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {isRequired && <Typography sx={{ color: '#E4080A', marginRight: '4px' }}>*</Typography>}
                                                        {row.name}
                                                        {row.tooltipInfo && (
                                                            <Tooltip
                                                                title={row.tooltipInfo}
                                                                arrow
                                                                componentsProps={{
                                                                    tooltip: { sx: { backgroundColor: '#272727', fontSize: '10px' } },
                                                                    arrow: { sx: { color: '#272727' } },
                                                                }}
                                                            >
                                                                <InfoOutlinedIcon
                                                                    sx={{
                                                                        fontSize: '16px',
                                                                        color: '#005CBC',
                                                                        marginLeft: '8px',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ padding: '12px 12px', width: '100px' }}>
                                                    <StyleTextField
                                                        value={row.mvalue}
                                                        onChange={(e) => handleSmartFilteringInputChange(index, 'mvalue', e.target.value)}
                                                        variant="filled"
                                                        sx={{
                                                            '&.MuiTextField-root': {
                                                                border: 'none',
                                                            },
                                                            width: '500px',
                                                            border: `1px solid ${smartFilteringErrors[index] ? '#d40020' : '#1378D4'}`,
                                                            opacity: isSmartFilteringEnabled ? 1 : 0.6,
                                                        }}
                                                        inputProps={{
                                                            style: { padding: '4px' },
                                                        }}
                                                        disabled={!isSmartFilteringEnabled}
                                                    />
                                                    {smartFilteringErrors[index] && (
                                                        <Typography sx={{ color: '#d40020', fontSize: '12px', marginTop: '4px' }}>
                                                            {smartFilteringErrors[index]}
                                                        </Typography>
                                                    )}
                                                    <Typography sx={{ fontSize: '10px', color: 'grey', mt: '4px' }}>{row.helpText}</Typography>
                                                </StyledTableCell>
                                            </TableRow>
                                        );
                                    })}
                                {isSegmentation && (
                                    <TableRow sx={{ height: '40px' }}>
                                        <StyledTableCell sx={{ padding: '12px 12px', width: '100%' }} colSpan={3}>
                                            <Typography
                                                sx={{
                                                    marginRight: '10px',
                                                    fontSize: '16px',
                                                    color: '#0047BA',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                Smart Sampling Automated Pipeline Configuration
                                            </Typography>
                                        </StyledTableCell>
                                    </TableRow>
                                )}
                                {isSegmentation &&
                                    smartSampling?.map((row, index) => (
                                        <TableRow key={index} sx={{ height: '40px' }}>
                                            <StyledTableCell sx={{ width: '40px', padding: '12px 12px' }} />
                                            <StyledTableCell sx={{ padding: '12px 12px', width: '200px' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography sx={{ color: '#E4080A', marginRight: '4px' }}>*</Typography>
                                                    {row.name}
                                                    {row.tooltipInfo && (
                                                        <Tooltip
                                                            title={row.tooltipInfo}
                                                            arrow
                                                            componentsProps={{
                                                                tooltip: { sx: { backgroundColor: '#272727', fontSize: '10px' } },
                                                                arrow: { sx: { color: '#272727' } },
                                                            }}
                                                        >
                                                            <InfoOutlinedIcon
                                                                sx={{
                                                                    fontSize: '16px',
                                                                    color: '#005CBC',
                                                                    marginLeft: '8px',
                                                                    cursor: 'pointer',
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ padding: '12px 12px', width: '100px' }}>
                                                <StyleTextField
                                                    value={row.mvalue}
                                                    onChange={(e) => handleSamplingInputChange(index, 'mvalue', e.target.value)}
                                                    variant="filled"
                                                    sx={{
                                                        '&.MuiTextField-root': {
                                                            border: 'none',
                                                        },
                                                        width: '200px',
                                                        border: `1px solid ${smartSamplingTagsErrors[`sampling_${index}`] ? '#d40020' : '#1378D4'}`,
                                                    }}
                                                    inputProps={{
                                                        style: { padding: '4px' },
                                                    }}
                                                />
                                                {smartSamplingTagsErrors[`sampling_${index}`] && (
                                                    <Typography sx={{ color: '#d40020', fontSize: '12px', marginTop: '4px' }}>
                                                        {smartSamplingTagsErrors[`sampling_${index}`]}
                                                    </Typography>
                                                )}
                                            </StyledTableCell>
                                        </TableRow>
                                    ))}
                                <TableRow sx={{ height: '40px' }}>
                                    <StyledTableCell sx={{ padding: '12px 12px', width: '100%' }} colSpan={3}>
                                        <Typography
                                            sx={{
                                                marginRight: '10px',
                                                fontSize: '16px',
                                                color: '#0047BA',
                                                fontWeight: '500',
                                            }}
                                        >
                                            Retraining Data Storage Configuration
                                        </Typography>
                                    </StyledTableCell>
                                </TableRow>
                                {retrainingTags?.map((row, index) => (
                                    <TableRow key={index} sx={{ height: '40px' }}>
                                        <StyledTableCell sx={{ width: '40px', padding: '12px 12px' }} />
                                        <StyledTableCell sx={{ padding: '12px 12px', width: '200px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography sx={{ color: '#E4080A', marginRight: '4px' }}>*</Typography>
                                                {row.name}
                                                {row.tooltipInfo && (
                                                    <Tooltip
                                                        title={row.tooltipInfo}
                                                        arrow
                                                        componentsProps={{
                                                            tooltip: { sx: { backgroundColor: '#272727', fontSize: '10px' } },
                                                            arrow: { sx: { color: '#272727' } },
                                                        }}
                                                    >
                                                        <InfoOutlinedIcon
                                                            sx={{
                                                                fontSize: '16px',
                                                                color: '#005CBC',
                                                                marginLeft: '8px',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ padding: '12px 12px', width: '100px' }}>
                                            <StyleTextField
                                                value={row.mvalue}
                                                onChange={(e) => handleRetrainingInputChange(index, 'mvalue', e.target.value)}
                                                variant="filled"
                                                sx={{
                                                    '&.MuiTextField-root': {
                                                        border: 'none',
                                                    },
                                                    width: '200px',
                                                    border: `1px solid ${retrainingTagsErrors[`retraining_${index}`] ? '#d40020' : '#1378D4'}`,
                                                }}
                                                inputProps={{
                                                    style: { padding: '4px' },
                                                }}
                                            />
                                            {retrainingTagsErrors[`retraining_${index}`] && (
                                                <Typography sx={{ color: '#d40020', fontSize: '12px', marginTop: '4px' }}>
                                                    {retrainingTagsErrors[`retraining_${index}`]}
                                                </Typography>
                                            )}
                                        </StyledTableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Box sx={{ display: 'flex', gap: '10px' }}>
                            <Button
                                sx={{ ...styles.modalSaveButton, height: '36px', marginTop: '50px' }}
                                disableElevation
                                disableRipple
                                variant="contained"
                                onClick={handleBack}
                            >
                                Back
                            </Button>
                            <Button
                                sx={{ ...styles.modalCancelButton, height: '36px', marginTop: '50px' }}
                                variant="outlined"
                                disableElevation
                                disableRipple
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                sx={{ ...styles.modalSaveButton, height: '36px', marginTop: '50px', marginLeft: '500px' }}
                                disableElevation
                                disableRipple
                                variant="contained"
                                onClick={handleSubmit}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Box>
                </Box>
                <Dialog
                    open={deleteOpenPopUp}
                    onClose={handleCancelDelete}
                    PaperProps={{
                        sx: {
                            width: '480px',
                            background: '#FFFFFF 0% 0% no-repeat padding-box',
                            boxShadow: '0px 0px 6px #00000074',
                            borderRadius: '4px',
                        },
                    }}
                >
                    <Box
                        sx={{
                            ...styles.modalWrapper,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <DialogTitle sx={styles.modalTitle}>Confirm Delete</DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={styles.modalContent}>Are you sure you want to delete the selected model tags?</DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'center', padding: '8px 16px', marginBottom: '20px' }}>
                            <Button
                                onClick={handleConfirmDelete}
                                disableElevation
                                disableRipple
                                sx={{ ...styles.modalSaveButton, width: '100px' }}
                                variant="contained"
                            >
                                Yes
                            </Button>
                            <Button
                                onClick={handleCancelDelete}
                                sx={{ ...styles.modalCancelButton, width: '100px' }}
                                variant="outlined"
                                disableElevation
                                disableRipple
                            >
                                No
                            </Button>
                        </DialogActions>
                    </Box>
                </Dialog>
            </Box>
        </ErrorBoundary>
    );
};

export default CreateNewModelTags;
